import { HorseService } from '$services/horse/horse.service';
import { IconsService } from '$services/icons/icons.service';
import { ContactRole, FormState, HorseDTO, UpdatePostForm } from '$types';
import { MaxSizeValidator } from '@angular-material-components/file-input';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators, ValidatorFn } from '@angular/forms';
import { FileInput } from 'ngx-material-file-input';

type errorOrNor = ValidationErrors | null | undefined;

@Component({
  selector: 'app-post-form',
  templateUrl: './post-form.component.html',
  styleUrls: ['./post-form.component.css']
})
export class PostFormComponent implements OnInit {
  @Input() entityForm?: FormGroup
  @Input() formState?: FormState
  @Output() onFormCompleted = new EventEmitter<UpdatePostForm>();
  @Output() onNextStep = new EventEmitter<void>();

  files_max_size: number = 2000; // 2 Mo
  has_correct_sire: boolean = false;
  has_no_sire: boolean = false;
  horse?: HorseDTO;
  imagesPreview: string[] = [];
  certificateOfSalePreview: string = '';
  certificationOfThePersonPreview: string = '';
  extractKbisPreview: string = '';
  horseRegistrationPreview: string = '';
  sireControl: AbstractControl<any, any> = new FormControl();
  horseControl: AbstractControl<any, any> = new FormControl();
  contactControl: AbstractControl<any, any> = new FormControl();
  documentsControl: AbstractControl<any, any> = new FormControl();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly horsesService: HorseService,
    private readonly icons: IconsService,) {
    this.icons.registerIcon(['file-import'])
    this.icons.registerIcon(['check'])
  }

  ngOnInit() {
    this.buildForm()

    if (!this.formState) {
      throw new Error("formState is mandatory")
    }

    this.formState.currentStep = 0
    this.formState.finished = false
    this.formState.showNext = false
    this.formState.steps = ['sire', 'horse_informations', 'contact_informations', 'documents', 'done']
  }

  private buildForm() {

    if (!this.entityForm) {
      throw new Error("form is not initialized");
    }

    this.entityForm?.addControl('sire', new FormControl('', Validators.pattern(/^(\d+[A-Za-z])?$/)))
    this.entityForm?.addControl('horse_informations', this.buildHorseForm())
    this.entityForm?.addControl('contact_informations', this.buildContactForm())
    this.entityForm?.addControl('documents', this.buildDocumentForm())

    this.sireControl = this.entityForm.get('sire')!
    this.horseControl = this.entityForm.get('horse_informations')!
    this.contactControl = this.entityForm.get('contact_informations')!
    this.documentsControl = this.entityForm.get('documents')!

    this.sireControl.valueChanges.subscribe((sire: string) => {
      if (this.entityForm) {
        this.getHorseInfosFromSire(sire)
      }
    })

    this.horseControl.get('horse_name')!.valueChanges.subscribe((nameValue: string) => {
      this.formState!.showNext = !!nameValue
      this.formState!.showSaveDraft = !!nameValue
    })

    this.horseControl.get('horse_photos')!.valueChanges.subscribe(
      async file => {
        this.imagesPreview = await this.getPhotoPreviewBase64()
      }
    )

    this.documentsControl.get('certificate_of_sale')!.valueChanges.subscribe(
      async file => {
        this.certificateOfSalePreview = await this.getDocumentPreviewBase64('certificate_of_sale')
      }
    )

    this.documentsControl.get('certification_of_the_person')!.valueChanges.subscribe(
      async file => {
        this.certificationOfThePersonPreview = await this.getDocumentPreviewBase64('certification_of_the_person')

      }
    )

    this.documentsControl.get('extract_kbis')!.valueChanges.subscribe(
      async file => {
        this.extractKbisPreview = await this.getDocumentPreviewBase64('extract_kbis')

      }
    )

    this.documentsControl.get('horse_registration')!.valueChanges.subscribe(
      async file => {
        this.horseRegistrationPreview = await this.getDocumentPreviewBase64('horse_registration')
      }
    )
  }

  hasNoSire() {
    this.has_no_sire = true;
    this.setHorse(null);
    this.enableHorseFields();
    this.onNextStep.emit();
  }

  private getHorseInfosFromSire(sire: string) {
    this.has_correct_sire = /^(\d+[A-Za-z])$/g.test(sire);
    if (this.has_correct_sire) {
      this.disableHorseFields();
      this.horsesService
        .findBySire(sire)
        .subscribe((horse) => {
          this.setHorse(horse);
          this.onNextStep.emit()
        });
    } else {
      this.horseControl.enable();
    }
  }

  private setHorse(horse: HorseDTO | null) {
    if (horse === null) {
      this.horseControl.get('horse_name')!.setValue('');
      this.horseControl.get('horse_sex')!.setValue('');
      this.horseControl.get('horse_year')!.setValue(2010);
      this.horseControl.get('horse_father')!.setValue('');
      this.horseControl.get('horse_mother')!.setValue('');
      return;
    }
    this.horse = horse;
    this.horseControl.get('horse_name')!.setValue(horse.name);
    this.horseControl.get('horse_sex')!.setValue(horse.sex.toLowerCase());
    this.horseControl.get('horse_year')!.setValue(this.horse?.birthYear);
    this.horseControl.get('horse_total_earn')!.setValue(horse.totalEarn);
    this.horseControl.get('horse_record')!.setValue(horse.raceRecord);
    this.horseControl.get('horse_father')!.setValue(horse.father);
    this.horseControl.get('horse_mother')!.setValue(horse.mother);
  }
  private disableHorseFields() {
    this.horseControl.get('horse_name')!.disable();
    this.horseControl.get('horse_sex')!.disable();
    this.horseControl.get('horse_year')!.disable();
    this.horseControl.get('horse_total_earn')!.disable();
    this.horseControl.get('horse_record')!.disable();
    this.horseControl.get('horse_father')!.disable();
    this.horseControl.get('horse_mother')!.disable();
  }
  private enableHorseFields() {
    this.horseControl.get('horse_name')!.enable();
    this.horseControl.get('horse_sex')!.enable();
    this.horseControl.get('horse_year')!.enable();
    this.horseControl.get('horse_total_earn')!.enable();
    this.horseControl.get('horse_record')!.enable();
    this.horseControl.get('horse_father')!.enable();
    this.horseControl.get('horse_mother')!.enable();
  }
  isMailConfirmated(): boolean {
    return !!this.contactControl.get('mail_confirmation')!.value
  }
  isEnglish(): boolean {
    return !!this.contactControl.get('contact_is_english')?.value
  }

  isAgent(): boolean {
    return this.documentsControl.get('role')!.value === ContactRole.AGENT;
  }

  isCoowner(): boolean {
    return this.documentsControl.get('role')!.value === ContactRole.COOWNER;;
  }
  getDocumentError(documentName: string): errorOrNor {
    return this.documentsControl.get(documentName)?.errors
  }
  getHorsePhotosErrors(): ValidationErrors | null | undefined {
    return this.horseControl.get('horse_photos')?.errors
  }
  isNegociating(): boolean {
    return !!this.horseControl.get('to_negociate')?.value
  }

  isTaxableVat(): boolean {
    return !!this.horseControl.get('taxable_vat')?.value
  }

  havePhotoSetted(): boolean {

    const photos: FileInput = this.horseControl.get('horse_photos')?.value

    return photos instanceof FileInput ? photos.files.length > 0 : false
  }

  haveDocumentSetted(documentName: string): boolean {

    const photos: FileInput = this.documentsControl.get(documentName)?.value

    return photos instanceof FileInput ? photos.files.length > 0 : false
  }

  maxDigitsValidator(control: AbstractControl): ValidationErrors | null {
    const maxDigits = 9;
    const value = control.value ? control.value.toString() : '';
    return value.length > maxDigits ? { maxDigits: true } : null;
  }


  isSireCorrectOrNoSire(): any {
    return (!!this.sireControl.value.trim() && this.has_correct_sire) || this.has_no_sire
  }

  getUploadErrors(errors: ValidationErrors | null | undefined): ValidationErrors {
    return errors ? errors : {};
  }

  private async getPhotoPreviewBase64(): Promise<string[]> {
    if (!this.havePhotoSetted()) {
      return []
    }

    const file: File[] = (this.horseControl.get('horse_photos')!.value as FileInput).files;
    return Promise.all(file
      .map(async (f) => {
        const { data } = await this.getImagePreview(f)

        return data as string;
      }));

  }
  private async getDocumentPreviewBase64(documentName: string): Promise<string> {
    if (!this.haveDocumentSetted(documentName)) {
      return ''
    }

    const file: File = (this.documentsControl.get(documentName)!.value as FileInput).files[0];
    const { data } = await this.getImagePreview(file)

    return data;

  }

  private async getImagePreview(file: File): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async (e: any) => {
        resolve({ data: reader.result })
      }
      reader.readAsDataURL(file)
    })
  }

  private buildDocumentForm() {

    const maxFileSizeValidator: ValidatorFn = MaxSizeValidator(this.files_max_size * 1024)
    const required: ValidationErrors | null = Validators.required

    return this.formBuilder.group({
      role: ['', required],
      certificate_of_sale: [maxFileSizeValidator],
      certification_of_the_person: [maxFileSizeValidator,],
      extract_kbis: [maxFileSizeValidator],
      horse_registration: [required, maxFileSizeValidator],
    });
  }

  private buildContactForm() {
    const required: ValidationErrors | null = Validators.required
    return this.formBuilder.group({
      last_name: ['', required],
      first_name: ['', required],
      landline_phone_number: ['',],
      mobile_phone_number: ['', required],
      email: ['', [required, Validators.email]],
      is_english: [false],
      mail_confirmation: [false],
    });
  }

  private buildHorseForm() {

    const required: ValidationErrors | null = Validators.required

    return this.formBuilder.group({
      horse_name: ['', required],
      horse_year: [2010, required],
      horse_sex: ['female', required],
      horse_total_earn: [0, required],
      horse_record: ['', required],
      horse_father: ['', required],
      horse_mother: ['', required],
      category: ['', required],
      horse_location: ['', required],
      horse_photos: [[]],//<- photo is not required
      sell_price: [0, [required, this.maxDigitsValidator]],
      to_negociate: [false],
      taxable_vat: [false],
      comments_fr: [''],
      comments_en: [''],
    });
  }
}
